import type { jsPDF } from "jspdf";
import moment from "moment";

export const COMPANY_DETAILS = {
  name: "SMEST CAPITAL PRIVATE LIMITED",
  addressLines: [
    "5, Prabhat Kunj, 24th Road, Khar (W),",
    "Mumbai 400052",
    "Maharashtra",
    "India",
  ],
  gstin: "27ABACS5302H1ZL",
  pan: "ABACS5302H",
  cin: "U65100MH2018PTC311416",
  bank: {
    beneficiary: "SMEST Capital Private Limited",
    bankName: "Kotak Mahindra Bank Limited, JVPD Scheme",
    accountNumber: "9813006261",
    ifsc: "KKBK0000661",
  },
  code: "SCPL",
};

export const EXTERNAL_DESCRIPTION_PREFIX =
  "Being your account debited towards incentive for clients dealing in";

export const PAGE_MARGIN = 14;

export const ensureUuid = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  const template = "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx";
  return template.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const buildDate = () => moment().format("YYYY-MM-DD");
export const buildDateTime = () => moment().format("YYYY-MM-DDTHH:mm");
export const buildInvoiceNumber = () => `${moment().format("DDMMYYYY")}-01`;

export const sanitizeFileName = (value: string, fallback: string) => {
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  return trimmed.replace(/[^A-Za-z0-9._-]+/g, "_");
};

export const parseAmount = (raw: string) => {
  if (!raw) return Number.NaN;
  const sanitized = raw.replace(/,/g, "").trim();
  if (!sanitized) return Number.NaN;
  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
};

export const roundCurrency = (value: number) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);

export const convertNumberToWords = (amount: number) => {
  if (!Number.isFinite(amount)) return "";

  const ones = [
    "Zero",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "Ten",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertHundreds = (num: number): string => {
    let result = "";
    const hundred = Math.floor(num / 100);
    const remainder = num % 100;

    if (hundred) {
      result += `${ones[hundred]} Hundred`;
      if (remainder) {
        result += " and ";
      }
    }

    if (remainder < 20) {
      if (remainder) {
        result += ones[remainder];
      }
    } else {
      const ten = Math.floor(remainder / 10);
      const unit = remainder % 10;
      result += tens[ten];
      if (unit) {
        result += ` ${ones[unit]}`;
      }
    }

    return result;
  };

  const convertToIndianWords = (num: number) => {
    if (num === 0) return "Zero";

    const units = ["", "Thousand", "Lakh", "Crore", "Arab"];
    const segments: string[] = [];
    let remainder = num;

    const firstChunk = remainder % 1000;
    remainder = Math.floor(remainder / 1000);
    if (firstChunk) {
      segments.unshift(convertHundreds(firstChunk));
    }

    for (let index = 1; remainder > 0 && index < units.length; index += 1) {
      const chunk = remainder % 100;
      remainder = Math.floor(remainder / 100);
      if (chunk) {
        const words = convertHundreds(chunk);
        segments.unshift(units[index] ? `${words} ${units[index]}` : words);
      }
    }

    if (remainder > 0) {
      segments.unshift(`${convertHundreds(remainder)} ${units[units.length - 1]}`);
    }

    return segments.join(" ").replace(/\s+/g, " ").trim();
  };

  const rupees = Math.floor(Math.abs(amount));
  let paise = Math.round((Math.abs(amount) - rupees) * 100);
  if (paise === 100) {
    paise = 0;
  }

  const rupeeWords = convertToIndianWords(rupees);
  const paiseWords =
    paise > 0 ? `${convertToIndianWords(paise)} Paise` : undefined;

  const prefix = amount < 0 ? "Negative " : "";
  const words = paiseWords
    ? `${prefix}Rupees ${rupeeWords} and ${paiseWords} Only`
    : `${prefix}Rupees ${rupeeWords} Only`;

  return words.replace(/\s+/g, " ").trim();
};

export const formatDate = (value: string, pattern: string) => {
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(pattern) : moment().format(pattern);
};

export const isValidDateValue = (value: string) => moment(value).isValid();

export const ensureSpace = (
  doc: jsPDF,
  currentY: number,
  required: number
) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + required <= pageHeight - PAGE_MARGIN) {
    return currentY;
  }
  doc.addPage();
  return PAGE_MARGIN;
};

export const drawKeyValue = (
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  labelWidth = 36,
  valueWidth?: number
) => {
  const adjustedY = ensureSpace(doc, y, 8);
  doc.setFont("helvetica", "bold");
  doc.text(`${label}:`, x, adjustedY);
  doc.setFont("helvetica", "normal");
  const textX = x + labelWidth;
  const available =
    valueWidth ?? doc.internal.pageSize.getWidth() - textX - PAGE_MARGIN;
  const lines = doc.splitTextToSize(value, Math.max(available, 10));
  doc.text(lines, textX, adjustedY);
  return adjustedY + lines.length * 5;
};
